import { useCallback, useEffect, useState } from 'react';
import api from 'services/api';
import { IGrupoEmail } from '../GerenciamentoEmailBase';

interface UseGerenciamentoEmailProps {
  tipo: string;
  montarPayload?: (dados: IGrupoEmail) => any;
}

interface UseGerenciamentoEmailReturn {
  gruposEmail: IGrupoEmail[];
  setGruposEmail: React.Dispatch<React.SetStateAction<IGrupoEmail[]>>;
  isLoading: boolean;
  criandoItem: boolean;
  buscar: () => Promise<void>;
  criar: (dados: IGrupoEmail) => Promise<any>;
  atualizar: (dados: IGrupoEmail) => Promise<any>;
  excluir: (id: number) => Promise<any>;
}

const useGerenciamentoEmail = ({
  tipo,
  montarPayload,
}: UseGerenciamentoEmailProps): UseGerenciamentoEmailReturn => {
  const [gruposEmail, setGruposEmail] = useState<IGrupoEmail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [criandoItem, setCriandoItem] = useState(false);

  const endpoint = `/grupo-usuarios-ata/gerenciar-email`;

  const buscar = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await api.get(`${endpoint}/${tipo}`);

      setGruposEmail(data);
    } catch (error) {
      console.error('Erro ao buscar grupos de email:', error);
    } finally {
      setIsLoading(false);
    }
  }, [endpoint]);

  const criar = async (dados: IGrupoEmail) => {
    try {
      setCriandoItem(true);

      const payload = montarPayload ? montarPayload(dados) : dados;

      await api.post(endpoint, {
        ...payload,
        tipo,
      });

      await buscar();
    } finally {
      setCriandoItem(false);
    }
  };

  const atualizar = async (dados: IGrupoEmail) => {
    if (!dados.id) {
      throw new Error('ID não informado.');
    }

    const payload = montarPayload ? montarPayload(dados) : dados;

    await api.put(`${endpoint}/${dados.id}`, {
      ...payload,
      tipo,
    });

    await buscar();
  };

  const excluir = async (id: number) => {
    await api.delete(`${endpoint}/${id}`);

    setGruposEmail(prev => prev.filter(item => item.id !== id));
  };

  useEffect(() => {
    buscar();
  }, [buscar]);

  return {
    gruposEmail,
    setGruposEmail,
    isLoading,
    criandoItem,
    buscar,
    criar,
    atualizar,
    excluir,
  };
};

export default useGerenciamentoEmail;
